// UI store — Zustand.
//
// Persists theme/density/accent to localStorage; persists the (optional)
// current-company selection to sessionStorage (so a new tab picks a fresh
// default, but a refresh keeps the choice within the session).
//
// go-pim scopes Products & Documents by company_id IN THE PATH, so the
// current-company fields are NOT load-bearing for v1 — they're kept (renamed
// from strata's currentMerchant*) so a future header-based CompanySwitcher can
// slot in without re-plumbing the store.
//
// Toast state is ephemeral (never persisted); toasts live here so any component
// can fire a toast and the App shell renders them via <ToastStack>.
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type Theme = "light" | "dark";
export type Density = "compact" | "regular" | "comfortable";

export interface ToastEntry {
  id: string;
  title: string;
  body?: string;
  /** Variant drives screen-reader announcement urgency in <ToastStack>.
   *  "success" (default) → role="status", aria-live="polite".
   *  "error" → role="alert", aria-live="assertive" (interrupts current speech). */
  variant?: "success" | "error";
}

interface UIState {
  theme: Theme;
  density: Density;
  accent: string;

  /** Selected company id — optional; v1 scopes by path, not this. */
  currentCompanyId: string | null;
  /** Selected company slug/display token, if a future switcher wants it. */
  currentCompanySlug: string | null;

  /** Ephemeral toast queue — rendered by <ToastStack> in App. Not persisted. */
  toasts: ToastEntry[];

  setTheme: (t: Theme) => void;
  setDensity: (d: Density) => void;
  setAccent: (hex: string) => void;
  /** Set the current company. Both id and slug move together. */
  setCurrentCompany: (id: string | null, slug: string | null) => void;
  /** Convenience for callers that only have the id. Sets slug=null. */
  setCurrentCompanyId: (id: string | null) => void;
  /** Push a toast onto the queue; auto-removes it after 4 seconds. */
  addToast: (toast: Omit<ToastEntry, "id">) => void;
  removeToast: (id: string) => void;
}

// Theme / density / accent — long-lived prefs.
export const useUI = create<UIState>()(
  persist(
    (set) => ({
      theme: "light",
      density: "regular",
      accent: "#2A6FDB",
      currentCompanyId: null,
      currentCompanySlug: null,
      toasts: [],
      setTheme: (theme) => set({ theme }),
      setDensity: (density) => set({ density }),
      setAccent: (accent) => set({ accent }),
      setCurrentCompany: (currentCompanyId, currentCompanySlug) =>
        set({ currentCompanyId, currentCompanySlug }),
      setCurrentCompanyId: (currentCompanyId) =>
        set({ currentCompanyId, currentCompanySlug: null }),
      addToast: (toast) => {
        const id = Math.random().toString(36).slice(2);
        set((s) => ({ toasts: [...s.toasts, { ...toast, id }] }));
        setTimeout(() => {
          set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
        }, 4000);
      },
      removeToast: (id) =>
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
    }),
    {
      name: "gopim.ui",
      storage: createJSONStorage(() => localStorage),
      // currentCompany* and toasts live outside persisted state.
      partialize: (s) => ({
        theme: s.theme,
        density: s.density,
        accent: s.accent,
      }),
    },
  ),
);

// Read currentCompany* from sessionStorage at boot; mirror future changes back.
const SESSION_COMPANY_KEY = "gopim.currentCompanyId";
const SESSION_COMPANY_SLUG_KEY = "gopim.currentCompanySlug";

if (typeof window !== "undefined") {
  try {
    const raw = sessionStorage.getItem(SESSION_COMPANY_KEY);
    const slugRaw = sessionStorage.getItem(SESSION_COMPANY_SLUG_KEY);
    if (raw) {
      useUI.setState({
        currentCompanyId: raw,
        currentCompanySlug: slugRaw && slugRaw.length > 0 ? slugRaw : null,
      });
    }
  } catch {
    // ignore — sessionStorage can be unavailable (private mode, embed contexts)
  }
  useUI.subscribe((state) => {
    try {
      if (state.currentCompanyId == null) {
        sessionStorage.removeItem(SESSION_COMPANY_KEY);
        sessionStorage.removeItem(SESSION_COMPANY_SLUG_KEY);
      } else {
        sessionStorage.setItem(SESSION_COMPANY_KEY, state.currentCompanyId);
        if (state.currentCompanySlug) {
          sessionStorage.setItem(
            SESSION_COMPANY_SLUG_KEY,
            state.currentCompanySlug,
          );
        } else {
          sessionStorage.removeItem(SESSION_COMPANY_SLUG_KEY);
        }
      }
    } catch {
      // ignore
    }
  });
}
